import { useEffect, useRef, useState } from "react";

interface CountUpProps {
  /** The final value to land on. */
  value: number;
  /** Printed before the number, e.g. "$". */
  prefix?: string;
  /** Printed after the number, e.g. "%". */
  suffix?: string;
  /** Decimal places, matched to how the figure was written. */
  fractionDigits?: number;
  /** Milliseconds to run. */
  duration?: number;
}

/** Decelerating ease — fast at the start, settling onto the final figure. */
function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

function prefersReducedMotion(): boolean {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
}

/**
 * Counts up to a figure once it scrolls into view.
 *
 * The animating text is hidden from assistive tech — a number rattling through
 * hundreds of intermediate values is noise to a screen reader — and the final
 * value is exposed alongside it instead. Reduced-motion users get the final
 * value with no animation.
 */
function CountUp({
  value,
  prefix = "",
  suffix = "",
  fractionDigits = 0,
  duration = 1600,
}: CountUpProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const [shown, setShown] = useState(value);
  const hasRun = useRef(false);

  const format = (n: number) =>
    `${prefix}${n.toLocaleString("en-US", {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    })}${suffix}`;

  useEffect(() => {
    const node = ref.current;
    if (!node || hasRun.current) return;

    if (prefersReducedMotion()) {
      setShown(value);
      return;
    }

    let frame = 0;
    const run = () => {
      hasRun.current = true;
      const start = performance.now();
      const step = (now: number) => {
        const t = Math.min((now - start) / duration, 1);
        setShown(value * easeOutCubic(t));
        if (t < 1) frame = requestAnimationFrame(step);
      };
      frame = requestAnimationFrame(step);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !hasRun.current) {
          run();
          observer.disconnect();
        }
      },
      { threshold: 0.4 }
    );

    setShown(0);
    observer.observe(node);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [value, duration]);

  return (
    <span className="countup" ref={ref}>
      {/* Hidden copy of the final figure holds the width open. Without it the
          number grows from one digit to eleven and rewraps the sentence on
          every frame. */}
      <span className="countup__sizer" aria-hidden="true">
        {format(value)}
      </span>
      <span className="countup__value" aria-hidden="true">
        {format(Math.round(shown))}
      </span>
      <span className="visually-hidden">{format(value)}</span>
    </span>
  );
}

export default CountUp;
